import { BadgeTone } from "stories/Admin";

/** Begravelsesbyrå fra GET /api/v1/funeral-agencies(/admin). */
export interface FuneralAgency {
  id: number;
  key: string;
  name: string;
  website: string | null;
  logoUrl: string | null;
  phone: string | null;
  email: string | null;
  memorialListUrl: string | null;
  memcareFuneralHome: string | null;
  aliases: string[];
  active: boolean;
  sortOrder: number;
}

export type SubmissionResult =
  | "CREATED"
  | "DUPLICATE"
  | "PENDING_REVIEW"
  | "REJECTED_NOT_NOTICE"
  | "REJECTED_BAD_IMAGE"
  | "RATE_LIMITED"
  | "ERROR";

/** Én opplasting fra appene (GET /api/v1/death-notices/admin/submissions). */
export interface DeathNoticeSubmission {
  id: number;
  createdAt: string;
  submittedBy: string | null;
  platform: string | null;
  appVersion: string | null;
  imageUrl: string | null;
  aiModel: string | null;
  confidence: number | null;
  isDeathNotice: boolean | null;
  agencyKey: string | null;
  agencyName: string | null;
  extractedName: string | null;
  result: SubmissionResult;
  reason: string | null;
  noticeId: number | null;
  noticeName: string | null;
  noticeStatus: "PUBLISHED" | "PENDING_REVIEW" | null;
  pushSent: boolean;
  durationMs: number | null;
  /** Bare i detaljen: AI-ens svar som JSON-tekst. */
  aiJson?: string | null;
}

export interface SubmissionPage {
  items: DeathNoticeSubmission[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export const RESULT_LABELS: Record<SubmissionResult, string> = {
  CREATED: "Publisert",
  DUPLICATE: "Fantes fra før",
  PENDING_REVIEW: "Venter på godkjenning",
  REJECTED_NOT_NOTICE: "Ikke en dødsannonse",
  REJECTED_BAD_IMAGE: "Ugyldig bilde",
  RATE_LIMITED: "For mange opplastinger",
  ERROR: "Feil",
};

export const RESULT_TONES: Record<SubmissionResult, BadgeTone> = {
  CREATED: "mint",
  DUPLICATE: "blue",
  PENDING_REVIEW: "gold",
  REJECTED_NOT_NOTICE: "grey",
  REJECTED_BAD_IMAGE: "grey",
  RATE_LIMITED: "lilac",
  ERROR: "rose",
};

export const RESULTS = Object.keys(RESULT_LABELS) as SubmissionResult[];

export const resultLabel = (r: string) =>
  RESULT_LABELS[r as SubmissionResult] ?? r;

export const resultTone = (r: string): BadgeTone =>
  RESULT_TONES[r as SubmissionResult] ?? "grey";

/** «0,93» (to desimaler, norsk komma), «–» når den mangler. */
export const formatConfidence = (c: number | null | undefined) =>
  typeof c === "number"
    ? c.toLocaleString("nb-NO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "–";

/** URL til AI-loggen. `page` er 1-basert her, 0-basert i API-et. */
export const submissionsUrl = (page: number, size: number, result?: string) =>
  `/api/v1/death-notices/admin/submissions?page=${Math.max(0, page - 1)}&size=${size}${
    result ? `&result=${encodeURIComponent(result)}` : ""
  }`;

/** AI-svaret pent formatert. Er det ikke gyldig JSON, vises teksten som den er. */
export const prettyJson = (raw: string | null | undefined) => {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

/** Alias-tekst → liste: skilt med komma eller linjeskift, uten tomme og doble (uavhengig av store/små bokstaver). */
export const parseAliases = (text: string): string[] => {
  const seen = new Set<string>();
  return text
    .split(/[,\n]/)
    .map((a) => a.trim())
    .filter((a) => {
      const key = a.toLowerCase();
      if (!a || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/** «ALJANNAH» fra «Al-Jannah» (bare A–Z, 0–9 og _). */
export const keyFromName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[æÆ]/g, "AE")
    .replace(/[øØ]/g, "O")
    .replace(/[åÅ]/g, "A")
    .toUpperCase()
    .replace(/BEGRAVELSESBYRA|BEGRAVELSE/g, "")
    .replace(/[^A-Z0-9]+/g, "")
    .slice(0, 32);

/** Flytter elementet på [index] ett steg opp (-1) eller ned (+1). Uendret når det ikke går. */
export const move = <T>(list: T[], index: number, delta: -1 | 1): T[] => {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length)
    return list;
  const next = list.slice();
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

/** Det som trengs for å sammenligne en lagret annonse med et utkast (felles for DeathNotice og utkastet). */
export interface NoticeFields {
  name: string;
  imageUrl: string | null;
  noticeImageUrl?: string | null;
  birthDate: string | null;
  deathDate: string | null;
  city: string | null;
  obituary: string | null;
  funeralInfo: string | null;
  ceremonies: {
    type: string;
    label: string;
    date: string | null;
    startTime: string | null;
    endTime: string | null;
    place: string | null;
    address: string | null;
    subtitle?: string | null;
    info?: string | null;
  }[];
  relatives: { name: string; relation: string | null; phone: string | null }[];
}

/** Én endring superadmin ser før lagring. `before`/`after` er null når noe legges til/fjernes. */
export interface NoticeChange {
  label: string;
  before: string | null;
  after: string | null;
}

const dmy = (v: string | null) => {
  if (!v) return null;
  const [y, m, d] = v.split("-");
  return d && m && y ? `${d}.${m}.${y}` : v;
};
const clean = (v: string | null | undefined) => (v ?? "").trim() || null;

/** «Fatiha / Dua 02.11.2026 16:30–19:00 · Central Jamaat, Motzfeldts gate 10 · For både menn og kvinner». */
export const describeCeremony = (c: NoticeFields["ceremonies"][number]) => {
  const time = c.startTime
    ? ` ${c.startTime.slice(0, 5)}${c.endTime ? `–${c.endTime.slice(0, 5)}` : ""}`
    : "";
  return [
    `${c.label.trim()} ${dmy(c.date) ?? "(uten dato)"}${time}`,
    [clean(c.place), clean(c.address)].filter(Boolean).join(", "),
    clean(c.subtitle),
    clean(c.info),
  ]
    .filter(Boolean)
    .join(" · ");
};

const describeRelative = (r: NoticeFields["relatives"][number]) =>
  [r.name.trim(), r.relation ? `(${r.relation})` : null, clean(r.phone)]
    .filter(Boolean)
    .join(" ");

/** Hva som endres i [before] om [after] lagres, i rekkefølgen superadmin leser skjemaet. */
export const noticeChanges = (
  before: NoticeFields,
  after: NoticeFields,
): NoticeChange[] => {
  const out: NoticeChange[] = [];
  const field = (
    label: string,
    a: string | null | undefined,
    b: string | null | undefined,
  ) => {
    const x = clean(a);
    const y = clean(b);
    if (x !== y) out.push({ label, before: x, after: y });
  };
  field("Navn", before.name, after.name);
  field("Født", dmy(before.birthDate), dmy(after.birthDate));
  field("Død", dmy(before.deathDate), dmy(after.deathDate));
  field("By", before.city, after.city);
  if (clean(before.imageUrl) !== clean(after.imageUrl))
    out.push({
      label: "Portrett",
      before: before.imageUrl ? "Bilde" : null,
      after: after.imageUrl ? "Nytt bilde" : null,
    });
  if (
    clean(after.noticeImageUrl) &&
    clean(before.noticeImageUrl) !== clean(after.noticeImageUrl)
  )
    out.push({
      label: "Plakatbilde",
      before: before.noticeImageUrl ? "Gammel plakat" : null,
      after: "Ny plakat",
    });

  // Seremonier: like linjer er uendret; en fjernet og en ny av samme type er en endring.
  const old = before.ceremonies.map((c) => ({ c, text: describeCeremony(c) }));
  const neu = after.ceremonies.map((c) => ({ c, text: describeCeremony(c) }));
  const removed = old.filter((o) => !neu.some((n) => n.text === o.text));
  const added = neu.filter((n) => !old.some((o) => o.text === n.text));
  for (const n of added) {
    const i = removed.findIndex(
      (o) =>
        o.c.type === n.c.type &&
        (o.c.date === n.c.date || !o.c.date || !n.c.date),
    );
    const o = i >= 0 ? removed.splice(i, 1)[0] : null;
    out.push({
      label: `Seremoni: ${n.c.label.trim()}`,
      before: o?.text ?? null,
      after: n.text,
    });
  }
  for (const o of removed)
    out.push({
      label: `Seremoni: ${o.c.label.trim()}`,
      before: o.text,
      after: null,
    });

  const oldRel = before.relatives.map(describeRelative);
  const newRel = after.relatives
    .filter((r) => r.name.trim())
    .map(describeRelative);
  const relAdded = newRel.filter((r) => !oldRel.includes(r));
  const relRemoved = oldRel.filter((r) => !newRel.includes(r));
  if (relAdded.length || relRemoved.length)
    out.push({
      label: "Pårørende",
      before: relRemoved.length ? relRemoved.join("\n") : null,
      after: relAdded.length ? relAdded.join("\n") : null,
    });

  field("Minnetekst", before.obituary, after.obituary);
  field("Informasjon om gravferden", before.funeralInfo, after.funeralInfo);
  return out;
};
